/**
 * Subida de un archivo de expediente al BFF aosenuma:
 *   1. POST /v1/upload/url     → presigned PUT R2 (5 min TTL)
 *   2. PUT  <upload_url>       → contenido binario directo a R2
 *   3. POST /v1/upload/complete → re-valida MIME magic-bytes, INSERT en
 *                                 public.expedientes (audit trigger emite evento)
 *
 * El INSERT a `expedientes` ocurre del lado servidor con `parcela_id` ya
 * resuelto a UUID. La UI siempre habla en código público (HUM-P####); el
 * mapeo se hace en el BFF.
 *
 * IDs UI ↔ schema BFF: `mapDocIdToTipoDoc()` cubre el drift histórico
 * entre los IDs que la captura usaba (`identificacion_oficial`,
 * `rfc_homoclave`, `acta_asamblea_delegacion`…) y los que `TipoDocSchema`
 * acepta en `lib-validation` (`ine`, `rfc`, `acta_asamblea`…).
 */
import { invalidateCache } from '../cacheBus';
import { bffFetch } from './bff';

/** Mapeo de IDs de la UI (captura checklist) a IDs canónicos del schema BFF.
 *  Si la UI manda un ID que ya es canónico, se devuelve tal cual. Para
 *  cualquier otro valor desconocido devolvemos `'otro'` para no bloquear
 *  el upload — captura de campo es laxa por diseño. */
const UI_TO_SCHEMA_TIPO_DOC: Record<string, string> = {
  // Titularidad
  acreditacion_titularidad_parcela: 'acreditacion_titularidad_parcela',
  certificado_parcelario:           'certificado_parcelario',
  constancia_derechos_ejidales:     'constancia_derechos_ejidales',
  // Agrario
  acta_asamblea_delegacion:    'acta_asamblea',
  constancia_no_adeudos_ejido: 'constancia_no_adeudos',
  // Identificación / datos personales
  identificacion_oficial: 'ine',
  curp:                   'curp',
  comprobante_domicilio:  'comprobante_domicilio',
  rfc_homoclave:          'rfc',
  acta_nacimiento:        'acta_nacimiento',
  acta_matrimonio:        'acta_matrimonio',
  datos_bancarios_clabe:  'datos_bancarios',
  // Predio (privado)
  escritura_publica: 'documento_propiedad',
  no_adeudo_predial: 'boleta_predial',
  // Contratos / fotos / paraguas
  contrato_arrendamiento: 'contrato_arrendamiento',
  contrato_firmado:       'contrato_firmado',
  foto_parcela:           'foto_parcela',
  foto_vista_aerea:       'foto_vista_aerea',
  foto_visita:            'foto_visita',
  otro:                   'otro',
  // Aliases ya canónicos (evitan que el fallback los mande a 'otro')
  ine:                    'ine',
  rfc:                    'rfc',
  acta_asamblea:          'acta_asamblea',
  constancia_no_adeudos:  'constancia_no_adeudos',
  datos_bancarios:        'datos_bancarios',
  documento_propiedad:    'documento_propiedad',
  boleta_predial:         'boleta_predial',
};

export function mapDocIdToTipoDoc(uiId: string): string {
  return UI_TO_SCHEMA_TIPO_DOC[uiId] ?? 'otro';
}

/**
 * Inverso para espejo Captura → Expedientes: en `expedientes.tipo_doc` vive el
 * ID canónico del BFF (`ine`, `rfc`, …) pero el checklist usa los IDs del
 * catálogo UI (`identificacion_oficial`, `rfc_homoclave`, …). Sin esto las
 * filas quedan en «Pendiente» pese a subir el archivo.
 */
export function mapSchemaTipoDocToCatalogDocId(schemaId: string): string {
  const SCHEMA_TO_CATALOG: Record<string, string> = {
    ine: 'identificacion_oficial',
    rfc: 'rfc_homoclave',
    acta_asamblea: 'acta_asamblea_delegacion',
    constancia_no_adeudos: 'constancia_no_adeudos_ejido',
    datos_bancarios: 'datos_bancarios_clabe',
    documento_propiedad: 'escritura_publica',
    boleta_predial: 'no_adeudo_predial',
  };
  return SCHEMA_TO_CATALOG[schemaId] ?? schemaId;
}

/** Inferimos el MIME a partir del File. Si el browser no lo conoce
 *  (algunos .heic/.docx vacíos), caemos al octet-stream — el BFF rechaza
 *  con `mime_no_permitido` y el técnico lo reintenta con otro PDF. */
export function inferMime(file: File): string {
  if (file.type) return file.type;
  const n = file.name.toLowerCase();
  if (n.endsWith('.pdf'))  return 'application/pdf';
  if (n.endsWith('.zip'))  return 'application/zip';
  if (n.endsWith('.jpg') || n.endsWith('.jpeg')) return 'image/jpeg';
  if (n.endsWith('.png'))  return 'image/png';
  if (n.endsWith('.webp')) return 'image/webp';
  if (n.endsWith('.heic')) return 'image/heic';
  if (n.endsWith('.docx')) return 'application/vnd.openxmlformats-officedocument.wordprocessingml.document';
  if (n.endsWith('.xlsx')) return 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
  return 'application/octet-stream';
}

async function sha256HexBlob(blob: Blob): Promise<string> {
  const buf = await blob.arrayBuffer();
  const hash = await crypto.subtle.digest('SHA-256', buf);
  return [...new Uint8Array(hash)].map((b) => b.toString(16).padStart(2, '0')).join('');
}

export interface UploadExpedienteResult {
  ok: true;
  expediente: { id: string; created_at: string };
  r2_key: string;
}

/**
 * Sube un archivo como expediente para una parcela (código público
 * `parcelaPublicId`, ej. "HUM-P0042"). El bucket por defecto es
 * `aosenuma-expedientes`; usar `aosenuma-evidencias` para fotos.
 *
 * Mapea el `tipoDoc` de la UI al ID canónico de TipoDocSchema antes de
 * llamar al BFF — si el ID es desconocido cae a `'otro'` para no
 * bloquear (captura laxa).
 *
 * Tras el INSERT exitoso, invalida `expedientes_list` y
 * `parcelas_tecnicas` para que ExpedientesPage / ExpedienteOverlay /
 * dashboard cliente vean el archivo nuevo sin esperar al TTL 30s.
 */
export async function uploadExpedienteFile(
  parcelaPublicId: string,
  file: File,
  tipoDoc: string,
  bucket: 'aosenuma-expedientes' | 'aosenuma-evidencias' = 'aosenuma-expedientes',
): Promise<UploadExpedienteResult> {
  const tipo_doc = mapDocIdToTipoDoc(tipoDoc);
  const mime = inferMime(file);

  // 1) Presigned PUT URL
  const { upload_url, r2_key } = await bffFetch<{ upload_url: string; r2_key: string }>(
    '/v1/upload/url',
    {
      method: 'POST',
      body: {
        parcela_id: parcelaPublicId,
        tipo_doc,
        filename: file.name,
        mime,
        size_bytes: file.size,
        bucket,
      },
    },
  );

  // 2) PUT directo a R2
  const putRes = await fetch(upload_url, {
    method: 'PUT',
    body: file,
    headers: { 'Content-Type': mime },
  });
  if (!putRes.ok) {
    throw new Error(`almacenamiento R2: HTTP ${putRes.status}`);
  }

  // 3) /v1/upload/complete: re-valida + INSERT a expedientes
  const sha256_hex = await sha256HexBlob(file);
  const result = await bffFetch<{ ok: true; expediente: { id: string; created_at: string } }>(
    '/v1/upload/complete',
    {
      method: 'POST',
      body: {
        parcela_id: parcelaPublicId,
        tipo_doc,
        bucket,
        r2_key,
        filename: file.name,
        mime,
        size_bytes: file.size,
        sha256_hex,
      },
    },
  );

  // Refetch en otras pestañas (cliente Elawan, dashboard) sin esperar TTL.
  invalidateCache(['expedientes_list', 'parcelas_tecnicas']);

  return { ok: true, expediente: result.expediente, r2_key };
}
